import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import PropertyModal from '../components/PropertyModal';
import LeaseModal from '../components/LeaseModal';
import DocumentsSection from '../components/DocumentsSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { differenceInDays, parseISO, format } from 'date-fns';

const { FiPlus, FiEdit2, FiTrash2, FiAlertCircle, FiFileText, FiDollarSign, FiZap, FiShield } = FiIcons;

const Properties = () => {
  const { data, addRecord, updateRecord, deleteRecord } = useStore();
  const [isPropModalOpen, setIsPropModalOpen] = useState(false);
  const [isLeaseModalOpen, setIsLeaseModalOpen] = useState(false);
  const [editingProp, setEditingProp] = useState(null);
  const [editingLease, setEditingLease] = useState(null);

  const handleSaveProperty = (propertyData) => {
    if (editingProp) {
      updateRecord('properties', editingProp.id, propertyData);
    } else {
      addRecord('properties', propertyData);
    }
  };

  const handleSaveLease = (leaseData) => {
    if (editingLease) {
      updateRecord('leases', editingLease.id, leaseData);
    } else {
      const leaseId = `l_${Date.now()}`;
      addRecord('leases', { ...leaseData, id: leaseId });
      // Update property to reference this as the current lease
      updateRecord('properties', leaseData.propertyId, { 
        currentLeaseId: leaseId,
        status: leaseData.status === 'Active' ? 'Occupied' : 'Vacant'
      });
    }
  };

  const getLeaseAlert = (lease) => {
    if (!lease?.endDate) return null;
    const days = differenceInDays(parseISO(lease.endDate), new Date());
    if (days < 0) return { label: 'Expired', color: 'bg-black text-white' };
    if (days <= 14) return { label: 'Urgent', color: 'bg-red-100 text-red-800' };
    if (days <= 30) return { label: 'Due Soon', color: 'bg-orange-100 text-orange-800' };
    return null;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Portfolio Inventory</h2>
          <p className="text-sm text-gray-500 font-medium">Manage units and active midterm contracts.</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => { setEditingLease(null); setIsLeaseModalOpen(true); }}
            className="flex items-center px-5 py-3 bg-indigo-50 text-indigo-700 rounded-2xl hover:bg-indigo-100 transition font-black text-sm uppercase tracking-wider"
          >
            <SafeIcon icon={FiFileText} className="mr-2" /> New Lease
          </button>
          <button 
            onClick={() => { setEditingProp(null); setIsPropModalOpen(true); }}
            className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider"
          >
            <SafeIcon icon={FiPlus} className="mr-2" /> Add Property
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {data.properties.map((prop) => {
          const currentLease = data.leases.find(l => l.id === prop.currentLeaseId);
          const tenant = data.tenants.find(t => t.id === currentLease?.tenantId);
          const alert = getLeaseAlert(currentLease);

          return (
            <div key={prop.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="flex flex-col lg:flex-row">
                {/* Property Info */}
                <div className="p-6 lg:w-1/3 border-b lg:border-b-0 lg:border-r border-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-lg ${
                      prop.status === 'Occupied' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {prop.status}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditingProp(prop); setIsPropModalOpen(true); }} className="p-2 text-gray-400 hover:text-indigo-600">
                        <SafeIcon icon={FiEdit2} size={16} />
                      </button>
                      <button onClick={() => deleteRecord('properties', prop.id)} className="p-2 text-gray-400 hover:text-red-500">
                        <SafeIcon icon={FiTrash2} size={16} />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-xl font-black text-gray-900">{prop.name}</h3>
                  <p className="text-xs text-gray-400 font-bold uppercase mt-1">{prop.address}</p>
                  
                  <div className="mt-6 flex items-center gap-4">
                    <div className="bg-gray-50 p-3 rounded-2xl flex-1">
                      <p className="text-[10px] font-black text-gray-400 uppercase">Base Rate</p>
                      <p className="text-lg font-black text-gray-900">${prop.rentRate}</p>
                    </div>
                    <button className="h-12 w-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-colors">
                      <SafeIcon icon={FiZap} />
                    </button>
                  </div>
                </div>

                {/* Lease Details */}
                <div className="p-6 flex-1 bg-gray-50/30">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                      <SafeIcon icon={FiFileText} className="text-indigo-600" /> Active Lease Information
                    </h4>
                    {currentLease && (
                      <button 
                        onClick={() => { setEditingLease(currentLease); setIsLeaseModalOpen(true); }}
                        className="text-[10px] font-black text-indigo-600 uppercase hover:underline"
                      >
                        Manage Contract
                      </button>
                    )}
                  </div>

                  {currentLease ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Contract Holder</p>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-lg flex items-center justify-center text-[10px] font-black">
                            {tenant?.name?.split(' ').map(n=>n[0]).join('') || '?'}
                          </div>
                          <div>
                            <p className="text-sm font-black text-gray-900">{tenant?.name}</p>
                            <p className="text-[10px] text-gray-500">{tenant?.email}</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Stay Period</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-black text-gray-900">
                            {format(parseISO(currentLease.startDate), 'MMM dd')} — {format(parseISO(currentLease.endDate), 'MMM dd, yyyy')}
                          </p>
                          {alert && (
                            <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${alert.color}`}>
                              {alert.label}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-500 font-bold mt-1 uppercase">
                          {differenceInDays(parseISO(currentLease.endDate), new Date())} Days Remaining
                        </p>
                      </div>

                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Financial Status</p>
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="flex items-center gap-1 text-sm font-black text-gray-900">
                              <SafeIcon icon={FiShield} size={12} className="text-green-500" />
                              ${currentLease.depositAmount}
                            </div>
                            <p className="text-[9px] text-gray-400 font-bold uppercase">Security Deposit</p>
                          </div>
                          <div className="w-px h-8 bg-gray-200"></div>
                          <div>
                            <div className="flex items-center gap-1 text-sm font-black text-indigo-600">
                              <SafeIcon icon={FiDollarSign} size={12} />
                              ${currentLease.rentAmount}
                            </div>
                            <p className="text-[9px] text-gray-400 font-bold uppercase">Actual Rent</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-24 flex flex-col items-center justify-center border-2 border-dashed border-gray-100 rounded-2xl bg-white/50">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">No Active Contract</p>
                      <button
                        onClick={() => {
                          setEditingLease({ propertyId: prop.id, startDate: format(new Date(), 'yyyy-MM-dd'), rentAmount: prop.rentRate || '' });
                          setIsLeaseModalOpen(true);
                        }}
                        className="text-[10px] font-black text-indigo-600 uppercase mt-2 hover:underline"
                      >
                        Start New Lease Workflow
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Documents */}
              <div className="px-6 py-4 border-t border-gray-50">
                <DocumentsSection recordType="property" recordId={prop.id} title="Property Documents" />
              </div>
            </div>
          )
        })}
      </div>

      <PropertyModal
        isOpen={isPropModalOpen}
        onClose={() => setIsPropModalOpen(false)}
        onSave={handleSaveProperty}
        initialData={editingProp}
        customFieldDefs={data.customFieldDefs || []}
      />
      
      <LeaseModal 
        isOpen={isLeaseModalOpen} 
        onClose={() => setIsLeaseModalOpen(false)} 
        onSave={handleSaveLease}
        tenants={data.tenants}
        properties={data.properties}
        initialData={editingLease}
      />
    </div>
  );
};

export default Properties;